/**
 * TeamsAppLayout Component
 *
 * Pattern: LAYOUT-TEAMS-APP
 * Purpose-built layout for Microsoft Teams Tab applications.
 *
 * USE THIS PATTERN FOR: Teams Tab apps ONLY.
 * For standalone web apps, admin portals, or dashboards, use LAYOUT-SIDEBAR-MODERN.
 *
 * Key Differences from LAYOUT-SIDEBAR-MODERN:
 * - No top header bar (Teams already provides a tab header)
 * - Brand header inside sidebar top (more compact, Teams-native feel)
 * - Sidebar shadow (shadow4) for depth against Teams' canvas
 * - User info + role badge + sign-out all in sidebar footer
 * - Smaller base font adapting to Teams' chat-density spacing
 * - Teams Purple (#6264A7) as default brand accent
 *
 * Structure:
 * ┌──────────────┬──────────────────────────────────────────────┐
 * │ [Logo + Name]│                                              │
 * │──────────────│                                              │
 * │ Navigation   │                                              │
 * │ - Item 1     │                                              │
 * │ - Item 2     │           Main Content Area                  │
 * │ - Item 3     │           (scrollable, 24px padding)         │
 * │              │                                              │
 * │ ─────────────│                                              │
 * │ Admin (pin)  │  ← Admin section pinned at bottom of nav     │
 * │ ─────────────│                                              │
 * │ [Avatar]     │                                              │
 * │ Name / Email │                                              │
 * │ [Role Badge] │                                              │
 * │ [Sign Out]   │                                              │
 * └──────────────┴──────────────────────────────────────────────┘
 *
 * Features:
 * - Fixed 260px sidebar with shadow4 elevation
 * - Brand header at top of sidebar (logo icon + product name)
 * - Role-based navigation with active/inactive icon states (Filled/Regular)
 * - Collapsible admin section with chevron animation
 * - User footer: avatar, display name, email, role badge, sign-out button
 * - Smooth hover/transition animations (0.2s)
 * - Full keyboard accessibility (tabIndex, onKeyDown, aria-expanded)
 * - Teams-native color palette via CSS custom properties
 *
 * Customization allowed:
 * - Navigation items (labels, icons, paths, visibility rules)
 * - Product name and logo icon
 * - Role badge colors and labels
 * - Admin sub-items
 * - Main content padding (default: 24px)
 *
 * Customization NOT allowed:
 * - Sidebar width (fixed: 260px)
 * - Spacing between nav items (fixed: 4px gap)
 * - Nav item padding (fixed: 10px 16px)
 * - Transition duration (fixed: 0.2s)
 * - Footer layout and spacing
 * - Brand header height and padding
 *
 * @see ui-patterns.registry.ts for style definitions
 * @see teams-theme.css for Teams-specific CSS overrides
 * @see teams-theme.ts for theme configuration constants
 */
import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  makeStyles,
  shorthands,
  tokens,
  Avatar,
  Text,
  Button,
  mergeClasses,
  Tooltip,
} from '@fluentui/react-components';
import {
  SignOutRegular,
  SettingsRegular,
  SettingsFilled,
  ChevronDownRegular,
  ChevronRightRegular,
  PanelLeftContractRegular,
  PanelLeftExpandRegular,
} from '@fluentui/react-icons';
import { AppBreadcrumbs } from '../common/Breadcrumbs';
import { brandColors, brandFonts } from '../../theme/brandColors';

// =============================================================================
// STYLES - LAYOUT-TEAMS-APP Pattern
// =============================================================================
// Deviation from the reference Teams-Purple sidebar: per the executive-briefing
// design (design-reference/executive-briefing.html) the sidebar is the Transformr
// navy (#011338) surface with white nav text and a #56ADDF@16% active state.
// Structure (brand top → nav → user footer) and the collapse rail are unchanged.

const useStyles = makeStyles({
  // Root container - horizontal flex for sidebar + content
  root: {
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: tokens.colorNeutralBackground2,
  },
  // Sidebar - full height, elevated with shadow. Width animates between the two
  // guide-sanctioned widths (260px expanded / 56px collapsed — see teams-app.md §3).
  sidebar: {
    width: '260px',
    backgroundColor: brandColors.navy,
    color: brandColors.white,
    fontFamily: brandFonts.body,
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.borderRight('1px', 'solid', 'rgba(255,255,255,0.08)'),
    flexShrink: 0,
    zIndex: 2,
    boxShadow: tokens.shadow16,
    transitionProperty: 'width',
    transitionDuration: '0.2s',
    transitionTimingFunction: 'ease',
  },
  // Deviation from the fixed-width reference: collapsible rail per the guide's
  // 56px collapsed state. Icon-only nav, tooltips supply the labels.
  sidebarCollapsed: {
    width: '56px',
  },
  // Brand header at top of sidebar
  header: {
    padding: '20px 24px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    ...shorthands.borderBottom('1px', 'solid', 'rgba(255,255,255,0.08)'),
  },
  // Brand lockup: "DevOps Reports" heading over a tiny uppercase "TRANSFORMR"
  // subheading, stacked beside the logo mark (per the briefing's .dva-brand-name).
  brandStack: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  brandName: {
    color: brandColors.white,
    fontFamily: brandFonts.heading,
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightBold,
    lineHeight: tokens.lineHeightBase400,
    letterSpacing: '-0.01em',
  },
  brandSub: {
    color: 'rgba(255,255,255,0.6)', // 10% brighter than the design's 0.5
    fontFamily: brandFonts.heading,
    fontSize: '0.5625rem', // 9px — below the smallest fontSize token
    fontWeight: tokens.fontWeightMedium,
    lineHeight: '1.15',
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
  },
  headerCollapsed: {
    padding: '16px 0',
    justifyContent: 'center',
    gap: '0',
  },
  collapseToggle: {
    marginLeft: 'auto',
  },
  navItemCollapsed: {
    justifyContent: 'center',
    paddingLeft: '0',
    paddingRight: '0',
  },
  navSubItemCollapsed: {
    justifyContent: 'center',
    paddingLeft: '0',
    paddingRight: '0',
  },
  footerCollapsed: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    paddingLeft: '0',
    paddingRight: '0',
  },
  logoIcon: {
    // Transformr "T" SVG is portrait (58.3×76.6). Size by height, auto width keeps
    // the aspect ratio; ~30px high reads at a similar weight to the old 24px icon.
    height: '1.6875rem', // 27px — 10% smaller
    width: 'auto',
    flexShrink: 0,
    display: 'block',
  },
  // Subtle buttons (collapse toggle, sign-out) sit on navy → force light text/icon.
  onNavyButton: {
    color: 'rgba(255,255,255,0.75)',
    ':hover': { color: brandColors.white, backgroundColor: 'rgba(255,255,255,0.08)' },
  },
  // Main navigation area (grows to fill space between header and footer)
  nav: {
    display: 'flex',
    flexDirection: 'column',
    padding: '16px 8px',
    gap: '4px',
    flexGrow: 1,
    overflowY: 'auto',
  },
  // Standard navigation item
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 16px',
    textDecoration: 'none',
    color: 'rgba(255,255,255,0.75)',
    fontWeight: tokens.fontWeightMedium,
    borderRadius: tokens.borderRadiusMedium,
    transitionProperty: 'all',
    transitionDuration: '0.2s',
    transitionTimingFunction: 'ease',
    ':hover': {
      backgroundColor: 'rgba(255,255,255,0.06)',
      color: brandColors.white,
    },
  },
  // Active navigation item — #56ADDF (blue-light) @16% fill, white text.
  navItemActive: {
    backgroundColor: 'rgba(86,173,223,0.16)',
    color: brandColors.white,
    fontWeight: tokens.fontWeightSemibold,
    ':hover': {
      backgroundColor: 'rgba(86,173,223,0.16)',
      color: brandColors.white,
    },
  },
  // Collapsible section container
  navSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  // Collapsible section header (e.g., Admin)
  navSectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 16px',
    cursor: 'pointer',
    color: 'rgba(255,255,255,0.75)',
    borderRadius: tokens.borderRadiusMedium,
    transitionProperty: 'all',
    transitionDuration: '0.2s',
    transitionTimingFunction: 'ease',
    ':hover': {
      backgroundColor: 'rgba(255,255,255,0.06)',
      color: brandColors.white,
    },
  },
  navSectionHeaderActive: {
    color: brandColors.white,
  },
  // Sub-navigation items (inside collapsible sections)
  navSubItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 16px 8px 44px',
    textDecoration: 'none',
    color: 'rgba(255,255,255,0.65)',
    borderRadius: tokens.borderRadiusMedium,
    fontSize: tokens.fontSizeBase200,
    transitionProperty: 'all',
    transitionDuration: '0.2s',
    transitionTimingFunction: 'ease',
    ':hover': {
      backgroundColor: 'rgba(255,255,255,0.06)',
      color: brandColors.white,
    },
  },
  navSubItemActive: {
    backgroundColor: 'rgba(86,173,223,0.16)',
    color: brandColors.white,
    fontWeight: tokens.fontWeightSemibold,
    ':hover': {
      backgroundColor: 'rgba(86,173,223,0.16)',
      color: brandColors.white,
    },
  },
  // Chevron icon for expandable sections
  chevron: {
    marginLeft: 'auto',
    transitionProperty: 'transform',
    transitionDuration: '0.2s',
    transitionTimingFunction: 'ease',
  },
  // Footer with user info and sign-out (at bottom of sidebar)
  footer: {
    padding: '16px',
    ...shorthands.borderTop('1px', 'solid', 'rgba(255,255,255,0.08)'),
    backgroundColor: 'transparent',
  },
  // User name on navy.
  userName: { color: brandColors.white },
  // User info container in footer
  userInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '12px',
  },
  userInfoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  userDetails: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.overflow('hidden'),
    flex: 1,
  },
  // Main column: fixed to the viewport height and does NOT scroll itself — the
  // breadcrumb stays pinned at the top and the routed content scrolls inside
  // `mainScroll`. This gives each page a bounded height so it can own independent
  // internal scroll regions (e.g. the epic report's content + detail pane).
  main: {
    flexGrow: 1,
    minWidth: 0,
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    // Lift the content column above the sidebar (a flex item with z-index:2) so
    // overlays anchored in content — e.g. the chart's hover card — render on top
    // of the navy menu rather than behind it.
    position: 'relative',
    zIndex: 3,
    ...shorthands.overflow('hidden'),
  },
  // The scrollable region beneath the pinned breadcrumb.
  mainScroll: {
    flexGrow: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: '24px',
  },
});

// =============================================================================
// TYPES
// =============================================================================

export interface TeamsNavItem {
  path: string;
  label: string;
  icon: React.ComponentType<{ fontSize?: number }>;
  activeIcon: React.ComponentType<{ fontSize?: number }>;
  /** Optional: claim active state for routes outside this item's own path
   *  subtree (e.g. a drill-down page that lives under a different URL). When it
   *  returns true the item wins the most-specific-match comparison. */
  activeMatch?: (pathname: string) => boolean;
}

export interface TeamsAdminSubItem {
  path: string;
  label: string;
  icon: React.ComponentType<{ fontSize?: number }>;
  activeIcon: React.ComponentType<{ fontSize?: number }>;
}

export interface TeamsUserInfo {
  displayName: string;
  email: string;
}

export interface TeamsAppLayoutProps {
  /** Product name displayed in sidebar header */
  productName: string;
  /** Small uppercase subheading beneath the product name (e.g. the org/brand). */
  productSubtitle?: string;
  /** Logo icon component */
  LogoIcon: React.ComponentType<{ className?: string }>;
  /** Navigation items - built dynamically based on user roles */
  navItems: TeamsNavItem[];
  /** Whether to show admin section */
  showAdminSection?: boolean;
  /** Admin sub-menu items */
  adminItems?: TeamsAdminSubItem[];
  /** Current user info */
  user?: TeamsUserInfo | null;
  /** User's display role (e.g., 'Admin', 'Editor', 'User') */
  userRole?: string;
  /** Role badge color */
  roleBadgeColor?: 'danger' | 'important' | 'brand' | 'subtle';
  /** Loading state */
  isLoading?: boolean;
  /** Sign out handler */
  onSignOut?: () => void | Promise<void>;
}

// =============================================================================
// COMPONENT
// =============================================================================

export const TeamsAppLayout: React.FC<TeamsAppLayoutProps> = ({
  productName,
  productSubtitle,
  LogoIcon,
  navItems,
  showAdminSection = false,
  adminItems = [],
  user,
  isLoading = false,
  onSignOut,
}) => {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const [adminExpanded, setAdminExpanded] = useState(true);

  // Collapsible rail (per teams-app.md §3 + TEAMS-APP-UX-GUIDE). Persisted so the
  // choice survives reloads.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem('devopsreports.sidebar.collapsed') === 'true'; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('devopsreports.sidebar.collapsed', String(collapsed)); }
    catch { /* ignore */ }
  }, [collapsed]);

  const handleSignOut = async () => {
    if (onSignOut) {
      await onSignOut();
    }
    navigate('/login');
  };

  // Check if any admin route is active
  const isAdminActive = location.pathname.startsWith('/admin');

  return (
    <div className={styles.root}>
      {/* Sidebar */}
      <aside className={mergeClasses(styles.sidebar, collapsed && styles.sidebarCollapsed)}>
        {/* Brand Header */}
        <div className={mergeClasses(styles.header, collapsed && styles.headerCollapsed)}>
          {!collapsed && <LogoIcon className={styles.logoIcon} />}
          {!collapsed && (
            <div className={styles.brandStack}>
              <Text as="span" className={styles.brandName}>{productName}</Text>
              {productSubtitle && <span className={styles.brandSub}>{productSubtitle}</span>}
            </div>
          )}
          <Tooltip content={collapsed ? 'Expand menu' : 'Collapse menu'} relationship="label" positioning="after">
            <Button
              appearance="subtle"
              className={mergeClasses(collapsed ? undefined : styles.collapseToggle, styles.onNavyButton)}
              icon={collapsed ? <PanelLeftExpandRegular /> : <PanelLeftContractRegular />}
              onClick={() => setCollapsed((c) => !c)}
              aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
            />
          </Tooltip>
        </div>

        {/* Main Navigation. Highlight only the most specific matching nav item, so
            e.g. /reports (Epic Reports) doesn't also light up on
            /reports/features. An item may also override via activeMatch. */}
        <nav className={styles.nav}>
          {navItems.map((item) => {
            const matchLen = (it: TeamsNavItem) => {
              // An explicit activeMatch wins outright (drill-down pages that live
              // under another item's URL subtree).
              if (it.activeMatch?.(location.pathname)) return Number.MAX_SAFE_INTEGER;
              const p = it.path;
              return p === '/'
                ? (location.pathname === '/' ? 1 : -1)
                : (location.pathname === p || location.pathname.startsWith(`${p}/`) ? p.length : -1);
            };
            const activeNavItem = navItems.reduce<TeamsNavItem | null>((best, it) => {
              const len = matchLen(it);
              return len >= 0 && (best === null || len > matchLen(best)) ? it : best;
            }, null);
            const isActive = item === activeNavItem;

            const Icon = isActive ? item.activeIcon : item.icon;

            const link = (
              <NavLink
                key={item.path}
                to={item.path}
                className={mergeClasses(
                  styles.navItem,
                  collapsed && styles.navItemCollapsed,
                  isActive && styles.navItemActive
                )}
                end={item.path === '/'}
              >
                <Icon fontSize={20} />
                {!collapsed && <Text>{item.label}</Text>}
              </NavLink>
            );

            return collapsed ? (
              <Tooltip key={item.path} content={item.label} relationship="label" positioning="after">
                {link}
              </Tooltip>
            ) : link;
          })}

          {/* Admin Section - at bottom of nav. Expanded: collapsible group with a
              chevron. Collapsed rail: render the sub-items as icon-only tooltipped
              links (no room for the chevron header). */}
          {showAdminSection && adminItems.length > 0 && (
            <div className={styles.navSection}>
              {!collapsed && (
                <div
                  className={mergeClasses(
                    styles.navSectionHeader,
                    isAdminActive && styles.navSectionHeaderActive
                  )}
                  onClick={() => setAdminExpanded(!adminExpanded)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      setAdminExpanded(!adminExpanded);
                    }
                  }}
                  aria-expanded={adminExpanded}
                >
                  {isAdminActive ? (
                    <SettingsFilled fontSize={20} />
                  ) : (
                    <SettingsRegular fontSize={20} />
                  )}
                  <Text>Admin</Text>
                  {adminExpanded ? (
                    <ChevronDownRegular className={styles.chevron} fontSize={16} />
                  ) : (
                    <ChevronRightRegular className={styles.chevron} fontSize={16} />
                  )}
                </div>
              )}
              {(collapsed || adminExpanded) && adminItems.map((subItem) => {
                const isSubActive = location.pathname === subItem.path;
                const SubIcon = isSubActive ? subItem.activeIcon : subItem.icon;

                const subLink = (
                  <NavLink
                    key={subItem.path}
                    to={subItem.path}
                    className={mergeClasses(
                      collapsed ? styles.navItem : styles.navSubItem,
                      collapsed && styles.navItemCollapsed,
                      isSubActive && (collapsed ? styles.navItemActive : styles.navSubItemActive)
                    )}
                  >
                    <SubIcon fontSize={collapsed ? 20 : 16} />
                    {!collapsed && <Text>{subItem.label}</Text>}
                  </NavLink>
                );

                return collapsed ? (
                  <Tooltip key={subItem.path} content={subItem.label} relationship="label" positioning="after">
                    {subLink}
                  </Tooltip>
                ) : subLink;
              })}
            </div>
          )}
        </nav>

        {/* User Footer */}
        <div className={mergeClasses(styles.footer, collapsed && styles.footerCollapsed)}>
          {user && (
            collapsed ? (
              <Tooltip
                content={user.displayName}
                relationship="label"
                positioning="after"
              >
                <Avatar name={user.displayName || 'User'} color="brand" size={32} />
              </Tooltip>
            ) : (
              <div className={styles.userInfo}>
                <div className={styles.userInfoRow}>
                  <Avatar
                    name={user.displayName || 'User'}
                    color="brand"
                    size={32}
                  />
                  <div className={styles.userDetails}>
                    <Text size={200} weight="semibold" truncate className={styles.userName}>
                      {user.displayName}
                    </Text>
                    <Text
                      size={200}
                      // On navy: muted white instead of the dark neutral foreground.
                      style={{ color: 'rgba(255,255,255,0.55)' }}
                      truncate
                    >
                      {user.email}
                    </Text>
                  </div>
                </div>
              </div>
            )
          )}
          {collapsed ? (
            <Tooltip content="Sign out" relationship="label" positioning="after">
              <Button
                appearance="subtle"
                className={styles.onNavyButton}
                icon={<SignOutRegular />}
                onClick={handleSignOut}
                disabled={isLoading}
                aria-label="Sign out"
              />
            </Tooltip>
          ) : (
            <Button
              appearance="subtle"
              className={styles.onNavyButton}
              icon={<SignOutRegular />}
              onClick={handleSignOut}
              disabled={isLoading}
              style={{ width: '100%', justifyContent: 'flex-start' }}
            >
              Sign Out
            </Button>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className={styles.main}>
        <AppBreadcrumbs />
        <div className={styles.mainScroll}>
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default TeamsAppLayout;
